import { useState, useEffect } from "react";
import { Modal, Spin } from "antd";
import { Trans } from "react-i18next";
import classNames from "classnames";
import { useAppDispatch } from "@hooks/redux";
import { fetchCollectionListThunk } from "@pages/library/librarySlice";
import Image from "@components/Image";
import { CommonButton } from "@components/CommonButton";
import { fetchVideoDetail } from "@pages/aiStudio/ScriptRewrite/scriptRewriteSlice";

interface VideoLibraryModalProps {
  open: boolean;
  onClose: () => void;
}

const VideoLibraryModal = ({ open, onClose }: VideoLibraryModalProps) => {
  const [libraryList, setLibraryList] = useState<any[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (open) {
      setLibraryLoading(true);
      setSelectedVideo(null);
      dispatch(fetchCollectionListThunk({ type: "1", data: {} }))
        .then((result: any) => {
          setLibraryList(result.payload || []);
        })
        .catch((error) => {
          console.error("Failed to fetch library list:", error);
        })
        .finally(() => {
          setLibraryLoading(false);
        });
    }
  }, [open, dispatch]);

  const handleSubmit = async () => {
    setSubmitLoading(true);
    try {
      await dispatch(
        fetchVideoDetail({
          id: selectedVideo.videoId,
          country: selectedVideo.country || "US",
          videoSource: selectedVideo.videoSource,
        }),
      );
      onClose();
      setSelectedVideo(null);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedVideo(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={982}
      title={
        <span className="text-kds-fg font-bold text-[16px]">
          <Trans>Library</Trans>
        </span>
      }
    >
      <Spin spinning={libraryLoading}>
        <div className="overflow-y-auto overflow-x-hidden h-[75vh] p-[4px] -m-[4px] -mr-[20px]">
          {libraryList.length > 0 ? (
            <div className="grid grid-cols-5 gap-[16px] w-[934px]">
              {libraryList.map((item: any) => (
                <div key={item.videoId ?? item.id} className="relative w-[174px]">
                  <div
                    className="relative cursor-pointer rounded-[8px] overflow-hidden transition-all"
                    onClick={() => setSelectedVideo(item)}
                  >
                    <Image url={item.coverImageUrl} className="w-full aspect-3/4 object-cover border-none!" />
                  </div>
                  <div
                    className={classNames(
                      "absolute z-10 -top-[4px] -right-[4px] -bottom-[4px] -left-[4px] border rounded-[8px] hover:border-kds-accent! pointer-events-none",
                      selectedVideo?.videoId === item.videoId ? "border-kds-accent!" : "border-transparent",
                    )}
                  ></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-[400px] text-kds-fg-soft">
              <Trans>Nothing here yet.</Trans>
            </div>
          )}
        </div>
      </Spin>
      <div className="flex justify-end gap-[12px] pt-[16px]">
        <CommonButton size="large" className="min-w-[100px]" onClick={handleCancel}>
          <Trans>Cancel</Trans>
        </CommonButton>
        <CommonButton
          size="large"
          type="primary"
          className="min-w-[100px]"
          disabled={!selectedVideo}
          loading={submitLoading}
          onClick={handleSubmit}
        >
          <Trans>Submit</Trans>
        </CommonButton>
      </div>
    </Modal>
  );
};

export default VideoLibraryModal;
